import { isBefore } from 'date-fns'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

import strapiApi from '~/@common/api/strapiApi'
import { routeName } from '~/@common/constants'
import authService from '~/@common/services/auth.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import type { StrapiApi } from '~/@common/types/api/strapi'
import NoticePopup from '~/@pages/@common/NoticePopup/NoticePopup'

export const usePopup = () => {
  // 학생 전환(대리 로그인 등) 시 effect가 재실행되도록 student.id를 직접 의존
  const loggedInStudentId = authService.isLoggedIn ? commonRepo.student?.id : undefined
  const { pathname } = useLocation()
  // 같은 학생에게 팝업이 중복 오픈되지 않도록 한 번 띄운 id를 기록
  const openedForStudentIdRef = useRef<typeof loggedInStudentId>(undefined)

  useEffect(() => {
    if (!loggedInStudentId) return
    // 점검 페이지에선 팝업 띄우지 않음
    if (pathname === routeName.serverMaintenance) return
    if (openedForStudentIdRef.current === loggedInStudentId) return
    // fetch 진행 중에 pathname 변경으로 effect가 재실행되어 동시에 두 번 fetch되는 걸 막기 위해 먼저 마킹
    openedForStudentIdRef.current = loggedInStudentId

    const storageKey = `noticePopupExpiresAt_${loggedInStudentId}`
    // localStorage 값이 깨진 경우(외부 조작/구버전 호환 등) effect가 중단되지 않도록 방어
    const storedPopups = (() => {
      try {
        const parsed = JSON.parse(localStorage.getItem(storageKey) ?? '[]')
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    })()

    // 1. localStorage에서 strapi fetch에서 제외할 팝업들을 구한다.
    const excludedPopups = storedPopups.filter(({ expiredDate }) => {
      return expiredDate === 'infinite' || isBefore(new Date(), new Date(expiredDate))
    })

    // 2. 제외된 팝업은 expiredDate가 지난 것들이 삭제된 최신의 값과 같으므로, 겸사겸사 localStorage값을 리뉴얼 시켜준다.
    localStorage.setItem(storageKey, JSON.stringify(excludedPopups))

    // 3. 현재 사용자의 플랜/사용자 타입 — Strapi 필드 targetPlanTypes/targetUserTypes와 매칭
    const currentPlanType: StrapiApi.NoticeTargetPlanType | null = (() => {
      if (commonRepo.permission.isPremium) return 'PREMIUM'
      if (commonRepo.permission.isBasic) return 'BASIC'
      if (commonRepo.permission.isSolo) return 'SOLO'
      return null
    })()
    const currentUserType: StrapiApi.NoticeTargetUserType = authService.userType

    // 현재 플랜 타입을 결정할 수 없으면 노출 대상이 없으므로 호출 자체를 스킵
    if (!currentPlanType) {
      openedForStudentIdRef.current = undefined
      return
    }

    // 4. Strapi 서버 사이드 필터로 plan/user 타입 매칭 — 매칭되지 않거나 필드가 null인 레코드는 서버에서 제외됨
    const excludedIds = excludedPopups.map(({ id }) => id)
    const filters: Record<string, unknown> = {
      targetPlanTypes: { $contains: currentPlanType },
      targetUserTypes: { $contains: currentUserType },
    }
    if (excludedIds.length > 0) {
      filters.id = { $notIn: excludedIds }
    }

    strapiApi
      .getNotice({
        sort: ['priority:desc'],
        filters,
        pagination: {
          limit: 1,
        },
      })
      .then((popups) => {
        if (popups.length > 0) {
          const popup = popups[0]
          modalService.openModal(
            { content: <NoticePopup {...popup} /> },
            {
              modalName: '팝업 공지 모달 - ' + popup.title,
            },
          )
        }
      })
      .catch((e) => {
        // fetch 실패 시 마킹 해제 — 다음 effect 재실행에서 동일 학생에 대해 재시도 허용
        if (openedForStudentIdRef.current === loggedInStudentId) {
          openedForStudentIdRef.current = undefined
        }
        console.error(e)
      })
  }, [loggedInStudentId, pathname])
}
